import { observable, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { ArrowRightIcon, FileDownIcon, BanIcon, SquareSigmaIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import {
    Field,
    FieldError,
    FieldLabel,
    RadioGroup,
    RadioGroupItem,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@campai/lib-web/components/new/ui'
import {
    AccountingPlanType,
    AccountingPlanTypeSchema,
    FinancesSettings,
    type AccountingMode,
} from '@campai/lib-common/entities'

import { Button } from './Button'
import { CreateOrganizationInput, Step } from './types'

type SelectAccountingPlanType = Exclude<AccountingPlanType, 'TEST'>

const accountingPlanTypes = Object.values(AccountingPlanTypeSchema.enum).filter(
    (planType): planType is SelectAccountingPlanType => planType !== 'TEST'
)

const accountingModeOptions: Array<{
    mode: AccountingMode
    icon: typeof SquareSigmaIcon
}> = [
    {
        mode: 'full',
        icon: SquareSigmaIcon,
    },
    {
        mode: 'export',
        icon: FileDownIcon,
    },
    {
        mode: 'none',
        icon: BanIcon,
    },
]

const Element = observer(({ input, onNext }: { input: CreateOrganizationInput; onNext: undefined | (() => void) }) => {
    const { finances, ui } = input
    const { t, i18n } = useTranslation()
    const i18nKey = 'createOrganization.accounting'
    const errors = ui.validationErrors
    const accountingMode = finances.accountingMode
    const isAccountingEnabled = accountingMode !== 'none'
    const getAccountingPlanLabel = (planType: SelectAccountingPlanType) =>
        t(`entities.finance.accounting.accountingPlanType.${planType}`)

    const setAccountingMode = (value: AccountingMode) => {
        if (value === 'none') {
            finances.accountingPlanType = null
        }

        finances.accountingMode = value
        ui.accountingModeSelected = true
    }

    return (
        <div>
            <div className="mb-2 text-xl font-medium">{t(`${i18nKey}.title`)}</div>
            <div className="text-base leading-7 text-neutral-600">{t(`${i18nKey}.description`)}</div>

            <div className="mt-6 space-y-5">
                <Field>
                    <RadioGroup
                        value={ui.accountingModeSelected ? accountingMode : ''}
                        className="flex flex-col gap-1.5"
                        onValueChange={(value) =>
                            runInAction(() => {
                                const nextAccountingMode = accountingModeOptions.find((option) => option.mode === value)

                                if (!nextAccountingMode) {
                                    return
                                }

                                setAccountingMode(nextAccountingMode.mode)
                            })
                        }
                    >
                        {accountingModeOptions.map(({ mode, icon: Icon }) => {
                            const isSelected = ui.accountingModeSelected && accountingMode === mode

                            return (
                                <label
                                    key={mode}
                                    htmlFor={`create-organization-accounting-mode-${mode}`}
                                    className={classNames(
                                        'flex cursor-pointer items-start gap-3 rounded-lg px-4 py-4 transition-all duration-200',
                                        {
                                            'bg-white/60 ring-1 ring-black': isSelected,
                                            'bg-white/20 hover:bg-white/30': !isSelected,
                                        }
                                    )}
                                >
                                    <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-neutral-950 shadow-sm">
                                        <Icon className="size-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="font-medium text-neutral-950">
                                                {t(`${i18nKey}.mode.options.${mode}.title`)}
                                            </div>
                                            <RadioGroupItem
                                                id={`create-organization-accounting-mode-${mode}`}
                                                value={mode}
                                                className="bg-white/80"
                                            />
                                        </div>
                                        <div className="text-sm leading-6 text-neutral-700">
                                            {t(`${i18nKey}.mode.options.${mode}.description`)}
                                        </div>
                                    </div>
                                </label>
                            )
                        })}
                    </RadioGroup>
                </Field>

                <div className="space-y-5">
                    <Field className={classNames({ 'opacity-55': !isAccountingEnabled })}>
                        <FieldLabel>{t(`${i18nKey}.plan.label`)}</FieldLabel>
                        <Select
                            disabled={!isAccountingEnabled}
                            value={finances.accountingPlanType ?? ''}
                            onValueChange={(value) =>
                                runInAction(() => {
                                    if (!value) {
                                        return
                                    }

                                    const accountingPlanType = accountingPlanTypes.find(
                                        (planType) => planType === value
                                    )

                                    if (!accountingPlanType) {
                                        return
                                    }

                                    finances.accountingPlanType = accountingPlanType
                                })
                            }
                        >
                            <SelectTrigger
                                className="h-11 border-neutral-200 bg-white disabled:opacity-100"
                                aria-invalid={!!errors?.getError('finances.accountingPlanType')}
                            >
                                <SelectValue>
                                    {finances.accountingPlanType
                                        ? getAccountingPlanLabel(finances.accountingPlanType)
                                        : t(`${i18nKey}.plan.placeholder`)}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    {accountingPlanTypes.map((planType) => (
                                        <SelectItem key={planType} value={planType}>
                                            {getAccountingPlanLabel(planType)}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        <FieldError>{errors?.getError('finances.accountingPlanType')}</FieldError>
                        <div className="mt-0 rounded-lg border border-amber-300 bg-amber-100 px-4 py-3 text-sm leading-6 text-amber-950">
                            {t(`${i18nKey}.plan.warning`)}
                        </div>
                    </Field>

                    <div className="grid gap-5 md:grid-cols-2">
                        <Field className={classNames({ 'opacity-55': !isAccountingEnabled })}>
                            <FieldLabel>{t(`${i18nKey}.fiscalMonth.label`)}</FieldLabel>
                            <Select
                                disabled={!isAccountingEnabled}
                                value={finances.fiscalMonthStart.toString()}
                                onValueChange={(value) =>
                                    runInAction(() => {
                                        if (!value) {
                                            return
                                        }

                                        finances.fiscalMonthStart = Number.parseInt(value, 10)
                                    })
                                }
                            >
                                <SelectTrigger className="h-11 border-neutral-200 bg-white disabled:opacity-100">
                                    <SelectValue>
                                        {new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(
                                            new Date(2000, finances.fiscalMonthStart - 1, 1)
                                        )}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {new Array(12).fill(null).map((_, month) => (
                                            <SelectItem key={month} value={(month + 1).toString()}>
                                                {new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(
                                                    new Date(2000, month, 1)
                                                )}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field className={classNames({ 'opacity-55': !isAccountingEnabled })}>
                            <FieldLabel>{t(`${i18nKey}.vatDueMethod.label`)}</FieldLabel>
                            <Select
                                disabled={!isAccountingEnabled}
                                value={finances.vatDueMethod}
                                onValueChange={(value) =>
                                    runInAction(() => {
                                        finances.vatDueMethod = value as FinancesSettings['vatDueMethod']
                                    })
                                }
                            >
                                <SelectTrigger className="h-11 border-neutral-200 bg-white disabled:opacity-100">
                                    <SelectValue>
                                        {t(`entities.finance.settings.vatDueMethod.${finances.vatDueMethod}`)}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem value="receipt">
                                            {t('entities.finance.settings.vatDueMethod.receipt')}
                                        </SelectItem>
                                        <SelectItem value="payment">
                                            {t('entities.finance.settings.vatDueMethod.payment')}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                </div>
            </div>

            <div className="mt-6 flex justify-center">
                <Button
                    disabled={!onNext}
                    icon={<ArrowRightIcon className="size-4" />}
                    mode="label"
                    onClick={onNext ?? undefined}
                >
                    {t(`${i18nKey}.continue`)}
                </Button>
            </div>
        </div>
    )
})

export const createAccounting = (input: CreateOrganizationInput): Step =>
    observable({
        id: 'accounting',
        Element,
        get isAvailable() {
            return true
        },
        get isValid() {
            if (!input.ui.accountingModeSelected) {
                return false
            }

            return input.finances.accountingMode === 'none' ? true : !!input.finances.accountingPlanType
        },
    })
